//@ts-strict-ignore
import React, { useEffect, useMemo, useState } from 'react';
import FileSelector from './FileSelector';
import EditableDataGrid from '../../components/EditableDataGrid';
import Formatters from '../../components/grid-addons/formatters';
import GridContainer from './GridContainer';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import { IRowUpdatedFunction } from '../EditableDataGrid/EditableDataGrid';
import { uuIdGenerator } from '../../utils/UuIdGenerator';
import { useDispatch } from 'react-redux';
import LinkFormatter from '../grid-addons/formatters/LinkFormatter';

export type IGridUploaderProps = {
  endPoint?: string;
  onChange?: Function;
  additionalColumns?: Array<IEditableDataGridColumnDef>;
  value: any;
  readOnly?: boolean;
  endPointView?: string;
};

const GridUploader = ({
  endPoint,
  onChange,
  additionalColumns,
  value: valueFromProps,
  readOnly,
  endPointView,
}: IGridUploaderProps) => {
  const [value, setValue] = useState('');

  const dispatch = useDispatch();

  const filesColumns = useMemo(() => {
    const columns: Array<IEditableDataGridColumnDef> = [
      {
        name: 'File Name',
        key: 'fileName',
        widthFactor: 2,
        editable: false,
        resizable: true,
      },
      {
        name: 'Comment',
        key: 'comment',
        widthFactor: 2,
        editable: true,
        resizable: true,
      },
      {
        key: 'fileName',
        name: '',
        widthFactor: 1,
        editable: false,
        resizable: false,
        sortable: false,
        formatter: LinkFormatter,
        options: {
          editInNewTab: true,
          url: endPointView as string,
          title: 'view file',
          formUrlFromFunction: (rowData) => {
            if (rowData.comment === 'Document Preview') {
              return '/api/resources/email_report_attachment/';
            }
            return endPointView;
          },
        },
        draggable: false,
      },
      {
        name: 'Mark as deleted',
        key: 'deleted',
        widthFactor: 1,
        formatter: Formatters.DeletedFormatter,
        options: { onGridChange: onChange },
        editable: false,
        resizable: true,
        draggable: false,
      },
    ];

    if (additionalColumns) {
      // add in one from the end
      columns.splice(-1, 0, ...additionalColumns);
    }
    if (readOnly) columns.pop();

    return columns;
  }, [endPointView, onChange, additionalColumns, readOnly]);

  const [gridValues, setGridValues] = useState(valueFromProps || []);

  useEffect(() => {
    setGridValues(valueFromProps || []);
  }, [valueFromProps]);

  const onFileChange = (value) => {
    const attachment = {
      fileName: value,
      comment: '',
      uuid: uuIdGenerator(),
    };
    if (!checkForFileName(value)) {
      const newArray = gridValues.slice();
      newArray.push(attachment);
      onChange?.(newArray);
    }
    setValue(value);
  };

  const checkForFileName = (value) => {
    let dontAdd = false;
    for (const file of gridValues) {
      if (file.fileName === value) {
        dontAdd = true;
      }
    }
    return dontAdd;
  };

  return (
    <div>
      {!readOnly ? (
        <FileSelector
          dataid="fileUpload"
          label="Upload File"
          onBlur={onFileChange}
          dispatch={dispatch}
          value={value}
          removeFileOption={!readOnly}
          endPoint={endPoint}
          excludeId={true}
        />
      ) : null}
      <GridContainer>
        <EditableDataGrid
          dataid={'gridUploader'}
          columns={filesColumns}
          enableCellSelect={!readOnly}
          enableCellAutoFocus={false}
          listData={gridValues}
          value={gridValues}
          rowsCount={gridValues.length}
          addBlankRow={false}
          onRowUpdated={onChange as IRowUpdatedFunction}
        />
      </GridContainer>
    </div>
  );
};

GridUploader.defaultProps = {
  endPointView: '/api/resources/attachment/',
};

export default GridUploader;
